import React, { FC, ReactNode, forwardRef, useState } from 'react'

// cn 유틸리티 함수 직접 구현
const cn = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(' ')
}

interface TooltipProviderProps {
  children: ReactNode
  delayDuration?: number
}

const TooltipProvider: FC<TooltipProviderProps> = ({
  children,
  delayDuration = 200,
}) => {
  return <>{children}</>
}

interface TooltipProps {
  children: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const Tooltip: FC<TooltipProps> = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(open)
    }
    onOpenChange?.(open)
  }

  return (
    <TooltipContext.Provider
      value={{ open: isOpen, onOpenChange: handleOpenChange }}
    >
      {children}
    </TooltipContext.Provider>
  )
}

interface TooltipContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TooltipContext = React.createContext<TooltipContextValue>({
  open: false,
  onOpenChange: () => {},
})

interface TooltipTriggerProps {
  children: ReactNode
  asChild?: boolean
}

const TooltipTrigger: FC<TooltipTriggerProps> = ({
  children,
  asChild = false,
}) => {
  const { open, onOpenChange } = React.useContext(TooltipContext)

  return (
    <div
      onMouseEnter={() => onOpenChange(true)}
      onMouseLeave={() => onOpenChange(false)}
      onFocus={() => onOpenChange(true)}
      onBlur={() => onOpenChange(false)}
    >
      {children}
    </div>
  )
}

interface TooltipContentProps {
  children: ReactNode
  className?: string
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
}

const TooltipContent = forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, sideOffset = 4, side = 'top', children, ...props }, ref) => {
    const { open } = React.useContext(TooltipContext)

    if (!open) return null

    // 간단한 위치 계산 로직
    const getPositionStyles = () => {
      switch (side) {
        case 'top':
          return {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: `${sideOffset}px`,
          }
        case 'bottom':
          return {
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: `${sideOffset}px`,
          }
        case 'left':
          return {
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginRight: `${sideOffset}px`,
          }
        case 'right':
          return {
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: `${sideOffset}px`,
          }
        default:
          return {}
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'z-50 overflow-hidden rounded-md border bg-white px-3 py-1.5 text-sm shadow-md',
          className
        )}
        style={{
          position: 'absolute',
          ...getPositionStyles(),
        }}
        {...props}
      >
        {children}
      </div>
    )
  }
)

TooltipContent.displayName = 'TooltipContent'

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
